import { z } from 'zod'
import { LANGUAGES, type Language } from './simulation'
import type { QuoteCreditsView } from './quotes'

/**
 * 전문가 검수 (로드맵 매출 8위, 2026-10-29). 사용자가 완성한 제품의 결과를 사람(운영자·식품 전문가)에게 검수받는다.
 * 1건 = 소모성 인앱 상품 1개. 구매는 견적권과 같은 quote_credits 표에 상품 id 로 구분해 쌓고(지급·환불 회수 코드를 같이 쓴다),
 * 검수에 쓰면 quote_credits.expert_review_id 를 채운다.
 *
 * 스토어 상품은 아직 없다. 대표가 App Store Connect·Play Console·RevenueCat 에 이 id 로 소모성 상품을 만든다.
 * id 는 환불 웹훅과 지급 동기화가 같이 보는 값이라 관리자 설정이 아니라 여기 한 곳에 둔다 — 설정으로 바꾸면 바꾸기 전에 산
 * 검수권을 환불 웹훅이 알아보지 못한다. 상품이 스토어에 없으면 앱이 가격을 못 받아 진입점을 그리지 않는다
 */
export const EXPERT_REVIEW_PRODUCT_ID = 'expert_review_credit'

/** 결제 필요 응답의 error.code (HTTP 402) */
export const EXPERT_REVIEW_PAYMENT_CODE = 'EXPERT_REVIEW_PAYMENT_REQUIRED'

/** 요청됨 → 진행 중(운영자가 맡음) → 완료(결과 전달) 또는 반려(검수권 반환) */
export const EXPERT_REVIEW_STATUSES = ['requested', 'in_progress', 'completed', 'rejected'] as const
export type ExpertReviewStatus = (typeof EXPERT_REVIEW_STATUSES)[number]

/** 아직 끝나지 않은 상태. 제품 하나에 열린 검수는 하나만 둔다 */
export const OPEN_EXPERT_REVIEW_STATUSES = [
  'requested',
  'in_progress',
] as const satisfies readonly ExpertReviewStatus[]

/** 반려 사유. operator = 운영자가 반려, expired = 처리 기한까지 아무도 맡지 않아 서버가 반려(lib/expert-reviews.ts) */
export const EXPERT_REVIEW_CLOSE_REASONS = ['operator', 'expired'] as const
export type ExpertReviewCloseReason = (typeof EXPERT_REVIEW_CLOSE_REASONS)[number]

/**
 * 검수 항목. 경쟁사(ReciPal) 전문가 검토 범위(영양값, 원재료 표기, 알레르기, 순중량·수율, 1회 제공량)에
 * 이 앱 결과에 있는 원가·공정을 더했다
 */
export const EXPERT_REVIEW_ITEMS = [
  'nutrition',
  'ingredient_list',
  'allergens',
  'net_quantity',
  'serving_size',
  'cost',
  'process',
] as const
export type ExpertReviewItem = (typeof EXPERT_REVIEW_ITEMS)[number]

/**
 * 요청 화면에 미리 골라 두는 항목 — 고르지 않고 바로 요청할 수 있게 (2026-10-30 대표 UX 원칙: 앞단은 심플하게).
 * 라벨 검토의 기본 범위(영양·원재료·알레르기·내용량·1회 제공량)이고, 원가·공정은 사용자가 더한다
 */
export const EXPERT_REVIEW_DEFAULT_ITEMS: readonly ExpertReviewItem[] = [
  'nutrition',
  'ingredient_list',
  'allergens',
  'net_quantity',
  'serving_size',
]

/** 항목 이름. 앱(4개 언어)과 관리자(ko)가 같이 쓴다 */
export const EXPERT_REVIEW_ITEM_LABELS: Record<ExpertReviewItem, Record<Language, string>> = {
  nutrition: { ko: '영양성분 값', en: 'Nutrition values', ja: '栄養成分の値', zh: '营养成分数值' },
  ingredient_list: {
    ko: '원재료 표기',
    en: 'Ingredient list',
    ja: '原材料表示',
    zh: '配料表标示',
  },
  allergens: { ko: '알레르기 표시', en: 'Allergens', ja: 'アレルゲン表示', zh: '过敏原标示' },
  net_quantity: {
    ko: '내용량·수율',
    en: 'Net quantity & yield',
    ja: '内容量・歩留まり',
    zh: '净含量与得率',
  },
  serving_size: { ko: '1회 제공량', en: 'Serving size', ja: '1回分の量', zh: '每份用量' },
  cost: { ko: '원가 계산', en: 'Cost estimate', ja: '原価計算', zh: '成本估算' },
  process: { ko: '제조 공정', en: 'Process steps', ja: '製造工程', zh: '生产工艺' },
}

/** 항목별 의견의 판정. ok = 문제 없음, fix = 고칠 것, note = 참고 */
export const EXPERT_REVIEW_VERDICTS = ['ok', 'fix', 'note'] as const
export type ExpertReviewVerdict = (typeof EXPERT_REVIEW_VERDICTS)[number]

export const EXPERT_REVIEW_VERDICT_LABELS: Record<ExpertReviewVerdict, Record<Language, string>> = {
  ok: { ko: '문제 없음', en: 'Looks good', ja: '問題なし', zh: '没有问题' },
  fix: { ko: '고칠 점', en: 'Needs a fix', ja: '修正が必要', zh: '需要修改' },
  note: { ko: '참고', en: 'Note', ja: '参考', zh: '参考' },
}

/**
 * 검수 결과가 법률 자문·인증·표시 적합을 보증하지 않는다는 고지. 요청 화면과 결과 화면에 그대로 보인다.
 * 식품 표시의 최종 책임은 영업자에게 있다(식품 등의 표시·광고에 관한 법률)
 */
export const EXPERT_REVIEW_DISCLAIMER: Record<Language, string> = {
  ko: '전문가 검수는 결과를 사람이 한 번 더 살펴보는 참고 의견이에요. 법률 자문이나 인증이 아니며, 표시 기준 적합·허가·인증을 보증하지 않아요. 제품 표시의 최종 책임은 판매하는 사업자에게 있어요.',
  en: 'Expert review is a second look by a person and is for reference only. It is not legal advice or certification and does not guarantee labeling compliance, approval or certification. The seller remains responsible for the final product label.',
  ja: '専門家レビューは結果を人がもう一度確認する参考意見です。法律相談や認証ではなく、表示基準への適合・許可・認証を保証しません。商品表示の最終的な責任は販売する事業者にあります。',
  zh: '专家审核是由人工再检查一遍结果的参考意见，不是法律咨询或认证，也不保证符合标示标准、获得许可或认证。产品标示的最终责任由销售方承担。',
}

/** POST /expert-reviews */
export const createExpertReviewSchema = z.object({
  simulationId: z.string().min(1),
  items: z
    .array(z.enum(EXPERT_REVIEW_ITEMS))
    .min(1)
    .max(EXPERT_REVIEW_ITEMS.length)
    .refine((items) => new Set(items).size === items.length, { message: 'Duplicate items' }),
  note: z.string().trim().max(1000).default(''),
  /** 알림 문구 언어 (앱 언어) */
  language: z.enum(LANGUAGES),
})
export type CreateExpertReview = z.infer<typeof createExpertReviewSchema>

export const expertReviewFindingSchema = z.object({
  item: z.enum(EXPERT_REVIEW_ITEMS),
  verdict: z.enum(EXPERT_REVIEW_VERDICTS),
  comment: z.string().trim().min(1).max(2000),
})
export type ExpertReviewFinding = z.infer<typeof expertReviewFindingSchema>

/** 첨부는 운영자가 올려 둔 문서의 https 주소다 (파일 업로드 저장소는 아직 없다) */
export const expertReviewAttachmentSchema = z.object({
  label: z.string().trim().min(1).max(100),
  url: z.url({ protocol: /^https$/ }).max(500),
})
export type ExpertReviewAttachment = z.infer<typeof expertReviewAttachmentSchema>

/** POST /admin/expert-reviews/:id/complete — 요청한 항목마다 의견 하나 */
export const adminCompleteExpertReviewSchema = z.object({
  findings: z.array(expertReviewFindingSchema).min(1).max(EXPERT_REVIEW_ITEMS.length),
  summary: z.string().trim().max(4000).default(''),
  attachments: z.array(expertReviewAttachmentSchema).max(5).default([]),
})
export type AdminCompleteExpertReview = z.infer<typeof adminCompleteExpertReviewSchema>

/** POST /admin/expert-reviews/:id/reject — 사유는 사용자에게 보인다 */
export const adminRejectExpertReviewSchema = z.object({
  reason: z.string().trim().min(1).max(1000),
})

/** GET /expert-reviews/credits. 견적권과 같은 모양이다 */
export type ExpertReviewCreditsView = QuoteCreditsView

export interface ExpertReviewView {
  id: string
  simulationId: string
  productName: string
  status: ExpertReviewStatus
  items: ExpertReviewItem[]
  note: string
  /** 완료일 때만 */
  findings: ExpertReviewFinding[]
  /** 완료면 총평, 반려면 사유 */
  summary: string
  attachments: ExpertReviewAttachment[]
  closeReason: ExpertReviewCloseReason | null
  /** 반려로 검수권을 돌려받았는지 */
  creditReturned: boolean
  createdAt: string
  /** 이때까지 아무도 맡지 않으면 서버가 반려하고 검수권을 돌려준다 */
  dueAt: string
  closedAt: string | null
}

export interface AdminExpertReviewView extends ExpertReviewView {
  userId: string
  language: Language
  assigneeEmail: string | null
  startedAt: string | null
}

/** 처리 기한 = 요청 시각 + slaDays 일 */
export function expertReviewDueAt(createdAt: Date, slaDays: number): Date {
  return new Date(createdAt.getTime() + slaDays * 24 * 60 * 60 * 1000)
}
